import { HttpClient } from '@angular/common/http';
import { Inject, Injectable } from '@angular/core';
import { Observable, of as observableOf } from 'rxjs';
import { catchError, map, tap } from 'rxjs/operators';
import { AuthService } from '../auth/auth.service';
import { AccountListApiRequestInterface, AccountNapDataApiInterface } from './account.api.interface';
import { Account } from './account.model';
import { AddonActivation } from './activation';

const API_URL = '/api/v1/account/';
const AJAX_URL = '/ajax/account-group/';

interface ListAddonActivationsResponse {
  activations: AddonActivation[];
}

@Injectable()
export class AccountsApiService {
  private nextUrl: string;

  constructor(
    private httpClient: HttpClient,
    private auth: AuthService,
    @Inject('AccountsAPIHost') private AccountsAPIHost: string,
  ) {}

  public get hasMore(): boolean {
    return Boolean(this.nextUrl);
  }

  get BASE_URL(): string {
    return `${this.AccountsAPIHost}/accounts.v1.AccountsService`;
  }

  listAddonActivations(appId: string, accountId: string): Observable<AddonActivation[]> {
    const url = `${this.BASE_URL}/ListAddonActivations`;
    return this.httpClient
      .post<ListAddonActivationsResponse>(url, { appId: appId, businessId: accountId }, { withCredentials: true })
      .pipe(
        catchError((err) => {
          if (err.status === 404) {
            return [];
          }
          throw err;
        }),
        map((data) => {
          if (data && data.activations) {
            return data.activations.map((activation) => new AddonActivation(activation));
          }
          return [];
        }),
      );
  }

  fetchAccount(appId: string, accountId: string): Observable<Account> {
    const url = `${API_URL}${accountId}`;
    return this.call(url, appId).pipe(
      map((data) => {
        return Account.fromApiModel(data.data);
      }),
    );
  }

  fetch(appId: string, partnerId?: string): Observable<Account[]> {
    this.nextUrl = null;
    let url;
    if (partnerId) {
      url = `${API_URL}${partnerId}/`;
    } else url = API_URL;
    return this.call(url, appId).pipe(
      tap((data) => (this.nextUrl = data.next_url)),
      map((data) => this.extractData(data)),
    );
  }

  fetchNext(appId: string): Observable<Account[]> {
    if (!this.nextUrl) {
      throw new Error('No next URL set. There is either no more data, or you need to call fetch first.');
    }

    // Right now this API is always served on the same domain as the URL, so we strip the host
    // from the nextUrl using this little trick to keep localhost to test proxying working
    const parser = document.createElement('a');
    parser.href = this.nextUrl;
    const nextUrl = parser.pathname + parser.search;

    return this.call(nextUrl, appId).pipe(
      tap((data) => (this.nextUrl = data.next_url)),
      map((data) => this.extractData(data)),
    );
  }

  public accountGroupNapDataGetMulti(accountGroupIds: string[]): Observable<AccountNapDataApiInterface[]> {
    let params: string;
    if (accountGroupIds && accountGroupIds.length) {
      params = accountGroupIds.join(',');
    } else {
      return observableOf([]);
    }
    return this.httpClient.get<AccountNapDataApiInterface[]>(`${AJAX_URL}get-multi-nap-data`, {
      params: { account_group_ids: params },
      headers: { 'Content-Type': 'application/json' },
      withCredentials: true,
    });
  }

  private call(url: string, appId: string): Observable<AccountListApiRequestInterface> {
    return this.httpClient.get<AccountListApiRequestInterface>(url, {
      params: { appId: appId },
      withCredentials: true,
    });
  }

  private extractData(data: any): Account[] {
    const accounts = [];
    for (const account of data.data) {
      accounts.push(Account.fromApiModel(account));
    }
    return accounts;
  }
}
